import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { investmentService } from "../../services/investmentService";
import type { InvestmentSummary as InvestmentSummaryType } from "../../types/models";
import { formatCurrency } from "../../utils/currency";
import { formatPercentDisplay } from "../../utils/numberFormat";
import { getApiErrorMessage } from "../../utils/apiError";

export function InvestmentSummary() {
  const { t } = useTranslation("investments");
  const [summary, setSummary] = useState<InvestmentSummaryType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [investorExpanded, setInvestorExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    investmentService
      .getInvestmentSummary()
      .then((data) => {
        if (!cancelled) {
          setSummary(data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = getApiErrorMessage(err, t("summary_error_loading"));
          setError(msg);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="w-8 h-8 border-2 border-brand-teal border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          {t("summary_loading")}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4 text-center">
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="py-4 text-center">
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{t("summary_no_data")}</p>
      </div>
    );
  }

  const availableCashNum = summary.available_cash;
  const cashColorClass =
    availableCashNum < 0
      ? "text-red-600 dark:text-red-400"
      : availableCashNum > 0
        ? "text-green-600 dark:text-green-400"
        : "text-neutral-900 dark:text-neutral-100";

  return (
    <div className="space-y-3">
      {/* Main totals: compact, smaller numbers */}
      <div className="grid grid-cols-1 gap-2">
        <div className="flex justify-between items-baseline gap-2">
          <span className="text-xs text-neutral-500 dark:text-neutral-400">{t("summary_total_external")}</span>
          <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
            {formatCurrency(summary.total_external)}
          </span>
        </div>
        <div className="flex justify-between items-baseline gap-2">
          <span className="text-xs text-neutral-500 dark:text-neutral-400">{t("summary_total_reinvested")}</span>
          <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
            {formatCurrency(summary.total_reinvested)}
          </span>
        </div>
        <div className="flex justify-between items-baseline gap-2 pt-1 border-t border-neutral-200 dark:border-neutral-700">
          <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300">
            {t("summary_available_cash")}
          </span>
          <span
            className={`text-sm font-semibold tabular-nums ${cashColorClass}`}
          >
            {formatCurrency(summary.available_cash)}
          </span>
        </div>
      </div>

      {/* Per-investor: compact table */}
      {summary.by_investor.length > 0 && (
        <div className="border-t border-neutral-200 dark:border-neutral-700 pt-2 mt-2">
          <button
            type="button"
            onClick={() => setInvestorExpanded((e) => !e)}
            aria-expanded={investorExpanded}
            className="flex items-center justify-between w-full text-left text-xs font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 py-1"
          >
            <span>{t("summary_per_investor")}</span>
            <span className="text-neutral-400" aria-hidden>
              {investorExpanded ? "▼" : "▶"}
            </span>
          </button>
          <div
            className={`mt-1.5 overflow-x-auto rounded-md border border-neutral-200 dark:border-neutral-700 ${investorExpanded ? "block" : "hidden md:block"}`}
          >
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-neutral-50 dark:bg-neutral-800/80 text-neutral-600 dark:text-neutral-400">
                  <th className="py-1.5 px-2 text-left font-medium">{t("field_investor")}</th>
                  <th className="py-1.5 px-2 text-right font-medium">{t("source_external")}</th>
                  <th className="py-1.5 px-2 text-right font-medium">{t("source_reinvested")}</th>
                  <th className="py-1.5 px-2 text-right font-medium">{t("summary_investor_envelope_remaining")}</th>
                  <th className="py-1.5 px-2 text-right font-medium">{t("summary_ownership_external_only")}</th>
                </tr>
              </thead>
              <tbody>
                {summary.by_investor.map((row) => (
                  <tr
                    key={row.name}
                    className="border-t border-neutral-200 dark:border-neutral-700/80 hover:bg-neutral-50/50 dark:hover:bg-neutral-800/30"
                  >
                    <td className="py-1.5 px-2 text-neutral-900 dark:text-neutral-100">
                      {row.name}
                    </td>
                    <td className="py-1.5 px-2 text-right tabular-nums text-neutral-700 dark:text-neutral-300">
                      {formatCurrency(row.external)}
                    </td>
                    <td className="py-1.5 px-2 text-right tabular-nums text-neutral-700 dark:text-neutral-300">
                      {formatCurrency(row.reinvested)}
                    </td>
                    <td className="py-1.5 px-2 text-right tabular-nums text-neutral-700 dark:text-neutral-300">
                      {formatCurrency(row.investor_envelope_remaining)}
                    </td>
                    <td className="py-1.5 px-2 text-right tabular-nums text-neutral-700 dark:text-neutral-300">
                      {formatPercentDisplay(row.ownership_percent)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1.5 px-2 leading-snug">
              {t("summary_investor_envelope_help")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
